import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AdminClientSummary } from '../../clients/index.js'
import type { InvitationResult } from '../index.js'
import { Modal } from '../../core/index.js'
import { MailPlus, Plus } from 'lucide-react'
import { InvitationHandOffBanner } from '../components/InviteOperatorModal.js'
import { ClientSelect, INPUT_CLASS, LINK_BUTTON, useActiveClients } from './formParts.js'
import { Field, FormError } from '../../core/index.js'
import { Button } from '../../core/index.js'
import type { AccountsAdminApi } from '../adminApi.js'
import type { ClientsAdminApi } from '../../clients/index.js'

/** The admin calls this screen uses. */
type AdminApi = AccountsAdminApi & ClientsAdminApi


const FORM_ID = 'new-operator-form'

interface OperatorForm {
  firstName: string
  lastName: string
  email: string
}

const EMPTY_FORM: OperatorForm = { firstName: '', lastName: '', email: '' }

interface NewOperatorModalProps {
  api: AdminApi
  /** The client the operator signs in to, when the form is opened from one; otherwise the admin picks. */
  client?: AdminClientSummary
  backLabel?: string
  onClose: () => void
}

/**
 * Invites an operator - a login with access to the whole client - through
 * `POST /admin/clients/{clientId}/operators/invitations` (invitations plan
 * phase 2): the login is made at once, switched off, and the person is
 * emailed a link and a code to set their own password with. Where the
 * deployment cannot email, the link is shown once here.
 */
export function NewOperatorModal({ api, client: fixedClient, backLabel, onClose }: NewOperatorModalProps) {
  const queryClient = useQueryClient()
  const clientsQuery = useActiveClients(api)
  const [registrationId, setRegistrationId] = useState(fixedClient?.registrationId ?? '')
  const client = fixedClient ?? clientsQuery.clients.find((candidate) => candidate.registrationId === registrationId) ?? null
  const [form, setForm] = useState<OperatorForm>(EMPTY_FORM)
  const [created, setCreated] = useState<InvitationResult | null>(null)

  const mutation = useMutation({
    mutationFn: (input: OperatorForm) => api.inviteOperator(client?.clientId ?? '', input),
    onSuccess: async (result) => {
      setCreated(result)
      // The client list's contact name can come from an operator, so both are refreshed.
      await queryClient.invalidateQueries({ queryKey: ['admin', 'operators'] })
      await queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      await queryClient.invalidateQueries({ queryKey: ['admin', 'operator-invitations'] })
    },
  })

  function update(changes: Partial<OperatorForm>): void {
    setForm((current) => ({ ...current, ...changes }))
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (!client?.clientId) {
      return
    }
    mutation.mutate({ firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim() })
  }

  function createAnother(): void {
    setCreated(null)
    mutation.reset()
    setForm(EMPTY_FORM)
  }

  const canSubmit = Boolean(client?.clientId) && form.firstName.trim() !== '' && form.lastName.trim() !== '' && form.email.trim() !== '' && !mutation.isPending

  return (
    <Modal
      eyebrow="Invite an operator"
      title={created ? 'Invitation sent' : 'Invite an operator'}
      subtitle={client?.organizationName}
      backLabel={backLabel}
      canClose={!mutation.isPending}
      onClose={onClose}
      footer={
        created ? (
          <div className="flex items-center gap-4">
            <Button icon={Plus} onClick={createAnother}>
              Invite another
            </Button>
            <button type="button" onClick={onClose} className={LINK_BUTTON}>
              Done
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <Button icon={MailPlus} type="submit" form={FORM_ID} disabled={!canSubmit}>
              {mutation.isPending ? 'Inviting…' : 'Invite operator'}
            </Button>
            <button type="button" onClick={onClose} disabled={mutation.isPending} className={LINK_BUTTON}>
              Cancel
            </button>
          </div>
        )
      }
    >
      {created ? (
        <div className="bg-canvas p-6 text-sm text-ink space-y-3">
          <p>
            <span className="font-bold">
              {created.invitation.firstName} {created.invitation.lastName}
            </span>{' '}
            has been invited as <span className="font-mono text-xs">{created.invitation.email}</span> with access to the whole of {client?.organizationName}. They set their own password from the invitation; until then the login is listed as invited.
          </p>
          <InvitationHandOffBanner result={created} onDismiss={() => setCreated({ ...created, handOff: null })} />
        </div>
      ) : (
        <form id={FORM_ID} onSubmit={handleSubmit} className="bg-canvas p-6 space-y-5">
          {!fixedClient && (
            <Field label="Client" htmlFor="new-operator-client" required>
              <ClientSelect id="new-operator-client" clients={clientsQuery.clients} isLoading={clientsQuery.isLoading} value={registrationId} onChange={setRegistrationId} />
            </Field>
          )}
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="First name" htmlFor="new-operator-first-name" required>
              <input id="new-operator-first-name" className={INPUT_CLASS} value={form.firstName} onChange={(event) => update({ firstName: event.target.value })} required autoFocus />
            </Field>
            <Field label="Last name" htmlFor="new-operator-last-name" required>
              <input id="new-operator-last-name" className={INPUT_CLASS} value={form.lastName} onChange={(event) => update({ lastName: event.target.value })} required />
            </Field>
          </div>
          <Field label="Login email" htmlFor="new-operator-email" required hint="Their username. Must be unique across the whole platform.">
            <input id="new-operator-email" type="email" className={INPUT_CLASS} value={form.email} onChange={(event) => update({ email: event.target.value })} required autoComplete="off" />
          </Field>
          <p className="text-[11px] text-ink-mid">An operator has full access to every facility of the client. Nobody sees a password: they are emailed a link and a code, good for seven days, and choose their own.</p>
          <FormError error={mutation.error} fallback="The operator could not be invited." />
        </form>
      )}
    </Modal>
  )
}
